import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { CircleCheck, Loader2, MapPin, PackageSearch, Search } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import AiCta from "../components/ai/AiCta";
import ShopStrip from "../components/home/ShopStrip";
import VideoHeroBg from "../components/service/VideoHeroBg";
import api from "../lib/api";

const FLOW = ["Order Created", "Pickup Scheduled", "Pickup Assigned", "Picked Up", "In Transit",
  "Reached Hub", "Out for Delivery", "Delivered"];

function statusColor(status) {
  if (status === "Delivered") return "bg-emerald-100 text-emerald-700";
  if (status === "NDR") return "bg-amber-100 text-amber-700";
  if (status?.startsWith("RTO")) return "bg-red-100 text-red-700";
  if (status === "Cancelled") return "bg-slate-200 text-slate-600";
  return "bg-blue-100 text-blue-700";
}

export function StatusBadge({ status }) {
  return <span data-testid={`status-badge-${status?.replace(/\s/g, "-")}`}
               className={`inline-flex rounded-full px-3 py-1 text-xs font-bold ${statusColor(status)}`}>{status}</span>;
}

function Timeline({ tracking }) {
  const events = [...(tracking || [])].sort((a, b) => (a.timestamp || "").localeCompare(b.timestamp || ""));
  return (
    <ol data-testid="tracking-timeline" className="relative ml-3 space-y-6 border-l-2 border-slate-200 pl-6">
      {events.map((ev, i) => {
        const last = i === events.length - 1;
        return (
          <li key={i} className="relative">
            <span className={`absolute -left-[31px] grid h-5 w-5 place-items-center rounded-full border-2 ${last ? "border-saffron bg-saffron" : "border-slate-300 bg-white"}`}>
              {last && <span className="h-1.5 w-1.5 rounded-full bg-white" />}
            </span>
            <p className={`font-heading text-sm font-bold ${last ? "text-saffron" : "text-navy"}`}>{ev.status}</p>
            {ev.description && <p className="mt-0.5 text-xs text-slate-600">{ev.description}</p>}
            <p className="mt-0.5 flex items-center gap-2 text-[11px] text-slate-400">
              {ev.location && <><MapPin className="h-3 w-3" />{ev.location}</>}
              {ev.timestamp && <span className="font-mono">{new Date(ev.timestamp).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</span>}
            </p>
          </li>
        );
      })}
    </ol>
  );
}

export default function TrackPage() {
  const { awb } = useParams();
  const navigate = useNavigate();
  const [input, setInput] = useState(awb || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchTrack = useCallback(async (num) => {
    if (!num) return;
    setLoading(true);
    setError("");
    setData(null);
    try {
      const res = await api.get(`/public/track/${num.trim().toUpperCase()}`);
      setData(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Tracking unavailable for this number right now.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (awb) fetchTrack(awb); }, [awb, fetchTrack]);

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      <section className="relative overflow-hidden bg-navy pb-16 pt-32 sm:pt-36" data-testid="track-hero">
        <VideoHeroBg mp4="/videos/bike.mp4" webm="/videos/bike.webm"
                     poster="/videos/bike_poster.jpg"
                     posterAlt="IndiaFast rider carrying a branded delivery box"
                     badge="Live Tracking" chip="AWB IFAST1000001 · In transit" testid="track-hero-video" />
        <div className="tricolor-beam absolute inset-x-0 top-0 h-1.5" />
        <div className="relative mx-auto max-w-5xl px-4 sm:px-6">
          <p className="animate-fade-up text-xs font-bold uppercase tracking-[0.2em] text-saffron">Shipment Tracking</p>
          <h1 className="animate-fade-up stagger-1 mt-2 font-heading text-3xl font-extrabold tracking-tight text-white sm:text-4xl">Where is my shipment?</h1>

          <form onSubmit={(e) => { e.preventDefault(); const v = input.trim(); if (v) { navigate(`/track/${v.toUpperCase()}`); } }}
                className="animate-fade-up stagger-2 mt-6 flex max-w-xl items-center gap-2 rounded-2xl border border-white/20 bg-white/95 p-2 shadow-2xl backdrop-blur">
            <Search className="ml-3 h-5 w-5 shrink-0 text-slate-400" />
            <input value={input} onChange={(e) => setInput(e.target.value)} data-testid="track-page-input"
                   placeholder="Enter AWB / Tracking Number e.g. IFAST1000001"
                   className="w-full bg-transparent py-2.5 text-sm text-navy outline-none" />
            <button type="submit" data-testid="track-page-submit-btn"
                    className="shrink-0 rounded-xl bg-saffron px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-saffron-hover">
              Track
            </button>
          </form>
          <div className="animate-fade-up stagger-3 mt-4"><AiCta label="Need help tracking?" action="start:track" testid="track-ask-ai-btn" /></div>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 pb-24 pt-10 sm:px-6">
        {loading && (
          <div className="mt-16 flex items-center justify-center gap-3 text-slate-500" data-testid="track-loading">
            <Loader2 className="h-5 w-5 animate-spin" /> Fetching live tracking…
          </div>
        )}
        {error && (
          <div data-testid="track-error" className="mt-10 rounded-2xl border border-red-200 bg-red-50 p-6 text-sm text-red-700">
            {error}
          </div>
        )}

        {data && (
          <div className="mt-10 grid gap-6 lg:grid-cols-5" data-testid="track-result">
            <div className="rounded-2xl border border-slate-200 bg-white p-6 lg:col-span-2">
              <div className="flex items-center justify-between">
                <p className="font-mono text-sm font-semibold text-navy">{data.awb}</p>
                <StatusBadge status={data.status} />
              </div>
              {data.is_international !== undefined && (
                <span className={`mt-2 inline-flex rounded-full px-2.5 py-0.5 text-[10px] font-bold ${data.is_international ? "bg-blue-100 text-blue-700" : "bg-orange-100 text-orange-700"}`} data-testid="track-type-badge">
                  {data.is_international ? "INTERNATIONAL" : "DOMESTIC"}
                </span>
              )}
              {data.source === "intl_live" && (
                <p className="mt-2 rounded-lg bg-blue-50 px-3 py-1.5 text-[11px] font-semibold text-blue-700" data-testid="track-intl-live-badge">Live international tracking · global carrier network</p>
              )}
              {data.source === "courier_live" && (
                <p className="mt-2 rounded-lg bg-emerald-50 px-3 py-1.5 text-[11px] font-semibold text-emerald-700" data-testid="track-domestic-live-badge">Live domestic tracking · INDIAFAST partner network</p>
              )}
              <div className="mt-5 space-y-3 text-sm">
                <div className="flex justify-between border-b border-slate-100 pb-3">
                  <span className="text-slate-500">Route</span>
                  <span className="font-semibold text-navy">{data.origin_city || "—"} → {data.destination_city || "—"}{data.destination_country && data.destination_country !== "India" ? `, ${data.destination_country}` : ""}</span>
                </div>
                {data.tracking_number && <div className="flex justify-between border-b border-slate-100 pb-3">
                  <span className="text-slate-500">Carrier AWB</span><span className="font-mono font-semibold">{data.tracking_number}</span>
                </div>}
                {data.receiver_name && <div className="flex justify-between border-b border-slate-100 pb-3" data-testid="track-receiver-name">
                  <span className="text-slate-500">Receiver</span><span className="font-semibold">{data.receiver_name}{data.receiver_city ? `, ${data.receiver_city}` : ""}{data.receiver_country ? ` (${data.receiver_country})` : ""}</span>
                </div>}
                {data.chargeable_weight && <div className="flex justify-between border-b border-slate-100 pb-3">
                  <span className="text-slate-500">Chargeable Weight</span><span className="font-mono font-semibold">{data.chargeable_weight} kg</span>
                </div>}
                <div className="flex justify-between">
                  <span className="text-slate-500">Expected Delivery</span>
                  <span className="font-semibold text-navy">{data.expected_delivery || "Updating soon"}</span>
                </div>
              </div>
              {data.status === "Delivered" && (
                <div className="mt-5 flex items-center gap-2 rounded-xl bg-emerald-50 p-3 text-xs font-semibold text-emerald-700">
                  <CircleCheck className="h-4 w-4" /> Proof of delivery captured digitally
                </div>
              )}
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-6 lg:col-span-3">
              <h2 className="mb-6 flex items-center gap-2 font-heading text-lg font-bold text-navy">
                <PackageSearch className="h-5 w-5 text-saffron" /> Shipment Journey
              </h2>
              {data.tracking?.length ? <Timeline tracking={data.tracking} /> : (
                <p className="text-sm text-slate-500">No scan events yet. Tracking will appear after pickup.</p>
              )}
            </div>
          </div>
        )}
      </div>
      <ShopStrip />
      <Footer />
    </div>
  );
}
